import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Dropdown, Form, Row } from 'react-bootstrap';
import CardDropdown from 'components/common/CardDropdown';
import Flex from 'components/common/Flex';
import ReferenceTableHeader from './ReferenceTableHeader';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import SubtleBadge from 'components/common/SubtleBadge';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from 'components/common/Avatar';
import PropTypes from 'prop-types';
import { useAuth } from 'nursd-dashboard/context/AuthContext';
import { getNurseDetails } from 'nursd-dashboard/api/nurseTable';
import UploadFile from './UploadFile';
import { uploadFile } from 'nursd-dashboard/utils/AWSHelper';
import { v4 as uuidv4 } from 'uuid';
import { postReference, updateReference } from 'nursd-dashboard/api/reference';
import { postNurseNotification } from 'nursd-dashboard/api/nurseNotification';
import { socket } from 'nursd-dashboard/api/socket';
import { sendNotification } from 'nursd-dashboard/api/webNotification';
import { toast } from 'react-toastify';
import {
  formatPhoneNumber,
  removeFormatting
} from 'nursd-dashboard/utils/function';

const ReferenceTable = ({ referenceData, nurseId, refresh, setRefresh }) => {
  const columns = [
    {
      accessorKey: 'fullName',
      header: 'Name',
      meta: {
        headerProps: { className: 'pe-1 text-900' },
        cellProps: {
          className: 'py-2'
        }
      },
      cell: ({ row: { original } }) => {
        const { fullName, avatar } = original;
        return (
          <Flex alignItems="center">
            <Avatar size="xl" name={avatar?.name} className="me-2" />
            <div className="flex-1">
              <h5 className="mb-0 fs-10">{fullName}</h5>
            </div>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'relationship',
      header: 'Relationship',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-900' },
        cellProps: { className: '' }
      },
      cell: ({ row: { original } }) => {
        const { relationship } = original;
        return <p className="pt-2">{relationship}</p>;
      }
    },
    {
      accessorKey: 'referenceFile',
      header: 'ReferenceFile',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-900' },
        cellProps: { className: '' }
      },
      cell: ({ row: { original } }) => {
        const { referenceFile } = original;
        return (
          <Flex direction="row" className="pt-2">
            {referenceFile !== '' ? (
              <p
                className="px-1"
                onClick={() => window.open(referenceFile, '_blank')}
                style={{
                  cursor: 'pointer'
                }}
              >
                File
                <FontAwesomeIcon
                  icon={'download'}
                  className="mx-2 text-primary"
                />
              </p>
            ) : (
              <p
                className="px-1"
                onClick={() => window.open(referenceFile, '_blank')}
                style={{
                  cursor: 'pointer'
                }}
              >
                -
              </p>
            )}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'email',
      header: 'Email',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original } }) => {
        const { email } = original;
        return <a href={`mailto:${email}`}>{email}</a>;
      }
    },
    {
      accessorKey: 'document_status',
      header: 'Status',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-900' },
        cellProps: { className: '' }
      },
      cell: ({ row: { original } }) => {
        const { document_status } = original;
        return (
          <SubtleBadge
            pill
            bg={
              document_status === 'waiting'
                ? 'primary'
                : document_status === 'Approved'
                ? 'success'
                : document_status === 'Not Approved'
                ? 'danger'
                : 'primary'
            }
          >
            {document_status}
          </SubtleBadge>
        );
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Submitted',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-900' },
        cellProps: { className: '' }
      },
      cell: ({ row: { original } }) => {
        const { createdAt } = original;
        return <p>{moment(createdAt).format('DD MMM,YYYY hh:mm a')}</p>;
      }
    },
    {
      accessorKey: 'none',
      header: '',
      enableSorting: false,
      meta: {
        cellProps: {
          className: 'text-end'
        }
      },
      cell: ({ row: { original } }) => {
        const { document_status } = original;
        return (
          <CardDropdown>
            {document_status !== 'Approved' ? (
              <div className="py-2">
                <Dropdown.Item>Approver</Dropdown.Item>
                <Dropdown.Item>Not Approved</Dropdown.Item>
              </div>
            ) : (
              <div className="py-2">
                <Dropdown.Item>---</Dropdown.Item>
              </div>
            )}
          </CardDropdown>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: referenceData,
    columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  const { authToken } = useAuth();
  const [nurse, setNurse] = useState(undefined);
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [uploadRef, setUploadRef] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    position: '',
    email: '',
    number: '',
    comments: ''
  });

  const [loading, setLoading] = React.useState(false);
  const [fileDoc, setFileDoc] = React.useState([]);

  const handleChange = ({ target }) => {
    let name = target.name;
    let value = target.value;
    setFormData({ ...formData, [name]: value });
  };

  const handleClose = () => {
    setFormData({
      name: '',
      relationship: '',
      position: '',
      email: '',
      number: '',
      comments: ''
    });
    setUploadRef(false);
    setFileDoc([]);
  };

  const handleRemoveDoc = path => {
    setFileDoc(fileDoc.filter(file => file.path !== path));
  };

  const postData = async file => {
    try {
      await postReference(
        authToken,
        formData.name,
        formData.relationship,
        formData.position,
        formData.email,
        removeFormatting(formData.number),
        '',
        file,
        '',
        Number(nurseId)
      ).then(async res => {
        await updateReference(authToken, res?.id, {
          document_status: 'Approved',
          verified: true,
          reviewerComments: formData?.comments
        })
          .then(async () => {
            await postNurseNotification(
              authToken,
              Number(nurseId),
              '',
              'Reference Uploaded',
              `Your Reference is uploaded by admin`,
              'MyReferenceScreen',
              {},
              false,
              false,
              'green',
              ''
            );
            socket?.emit('tableNotificationInsert', {
              table: 'NurseNotificationTable',
              id: Number(nurseId)
            });
            if (nurse?.mobileId) {
              sendNotification(authToken, {
                expoPushToken: nurse?.mobileId,
                title: 'Reference Uploaded',
                body: `Your Reference is uploaded by admin`,
                data: { screen: 'MyReferenceScreen' }
              });
            }
            toast.success(`Successfully uploaded the reference.`, {
              theme: 'colored'
            });
            setRefresh(!refresh);
            setFormData({
              name: '',
              relationship: '',
              position: '',
              email: '',
              number: '',
              comments: ''
            });
            setUploadRef(false);
            setFileDoc([]);
          })
          .catch(e => {
            console.log(e);
          });
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getBlob = async uri => {
    const response = await fetch(uri);
    const blob = await response.blob();
    return blob;
  };

  const blobToUint8Array = blob => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const uint8Array = new Uint8Array(reader.result);
        resolve(uint8Array);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  };

  // Function to get the Uint8Array from the file
  const getFileAsUint8Array = async file => {
    const uri = file.preview; // Get the URI from the file's preview property
    const blob = await getBlob(uri); // Convert URI to Blob
    const uint8Array = await blobToUint8Array(blob); // Convert Blob to Uint8Array
    return uint8Array;
  };

  // Example usage: After a file is dropped, convert it to Uint8Array
  const handleConvert = async file => {
    if (file && file[0]) {
      const uint8Array = await getFileAsUint8Array(file[0]);
      return uint8Array; // Do something with the Uint8Array
    }
  };

  const postNurseReference = async () => {
    setLoading(true);
    if (fileDoc.length !== 0) {
      // const blob = await getBlob(fileDoc);
      // const buffer = await blobToUint8Array(blob);
      const buffer = await handleConvert(fileDoc);

      let name = `${nurse?.uniqueId}/reference-${uuidv4()}.pdf`;
      // console.log(buffer, name)
      const key = await uploadFile(authToken, name, buffer, '');
      postData(key?.key);
    } else {
      postData('');
    }
  };

  const checkUser = async (authToken, id) => {
    try {
      await getNurseDetails(authToken, id).then(res => {
        if (res === undefined) {
          setNurse(null);
          setLoadingScreen(false);
        } else {
          setNurse(res);
          setLoadingScreen(false);
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (nurseId !== null) {
      checkUser(authToken, nurseId);
    }
  }, [nurseId]);

  const handleSubmit = event => {
    event.preventDefault(); // Prevent default form submission behavior
    postNurseReference();
  };

  return (
    <>
      {loadingScreen ? (
        <p className="mt-2">Loading...</p>
      ) : (
        <AdvanceTableProvider {...table}>
          <Card className="mb-3">
            <Card.Header>
              <ReferenceTableHeader
                reference={referenceData}
                uploadRef={uploadRef}
                setUploadRef={setUploadRef}
              />
            </Card.Header>
            {uploadRef ? (
              <div>
                <Form onSubmit={handleSubmit}>
                  <div className="p-x1">
                    <Row className="mb-2">
                      <Form.Group
                        as={Col}
                        md="6"
                        className="mb-2"
                        controlId="name"
                      >
                        <Form.Label className="fs-9">Reference Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="Name"
                          required
                          value={formData?.name}
                          onChange={handleChange}
                        />
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        md="6"
                        className="mb-2"
                        controlId="relationship"
                      >
                        <Form.Label className="fs-9">
                          Reference Relationship
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="relationship"
                          placeholder="Relationship"
                          required
                          value={formData?.relationship}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group
                        as={Col}
                        md="6"
                        className="mb-2"
                        controlId="position"
                      >
                        <Form.Label className="fs-9">
                          Reference Position
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="position"
                          placeholder="Position"
                          required
                          value={formData?.position}
                          onChange={handleChange}
                        />
                      </Form.Group>
                      <Form.Group
                        as={Col}
                        md="6"
                        className="mb-2"
                        controlId="email"
                      >
                        <Form.Label className="fs-9">
                          Reference Email
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="email"
                          placeholder="Email"
                          required
                          value={formData?.email}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group
                        as={Col}
                        md="6"
                        className="mb-2"
                        controlId="number"
                      >
                        <Form.Label className="fs-9">
                          Reference Number
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="number"
                          placeholder="Number"
                          required
                          value={formatPhoneNumber(formData?.number)}
                          onChange={handleChange}
                          // pattern="[0-9]*"
                        />
                      </Form.Group>
                    </Row>

                    <Form.Group className="mb-2" controlId="comments">
                      <Form.Label className="fs-9">Comments</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="comments"
                        onChange={handleChange}
                      />
                    </Form.Group>

                    <Col md="6">
                      <UploadFile
                        file={fileDoc}
                        setFile={setFileDoc}
                        handleRemove={handleRemoveDoc}
                      />
                    </Col>
                  </div>
                  <div className="px-x1 border-top-0 mb-4">
                    <Button
                      variant="primary"
                      type="submit"
                      className="px-4 mx-0"
                    >
                      {loading ? 'Loading...' : 'Upload'}
                    </Button>
                    <Button
                      variant="secondary"
                      type="submit"
                      className="px-4 mx-2"
                      onClick={() => handleClose()}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              </div>
            ) : referenceData?.length === 0 ? (
              <div className="m-3">
                <p>No Data Available</p>
              </div>
            ) : (
              <>
                <Card.Body className="p-0">
                  <AdvanceTable
                    headerClassName="bg-200 text-nowrap align-middle"
                    rowClassName="align-middle white-space-nowrap"
                    tableProps={{
                      size: 'sm',
                      striped: true,
                      className: 'fs-10 mb-0 overflow-hidden'
                    }}
                  />
                </Card.Body>
                <Card.Footer>
                  <div className="mt-3">
                    <AdvanceTableFooter
                      rowInfo
                      navButtons
                      rowsPerPageSelection
                    />
                  </div>
                </Card.Footer>
              </>
            )}
          </Card>
        </AdvanceTableProvider>
      )}
    </>
  );
};

ReferenceTable.propTypes = {
  referenceData: PropTypes.any,
  nurseId: PropTypes.any,
  refresh: PropTypes.any,
  setRefresh: PropTypes.any
};

export default ReferenceTable;
